import { useCallback, useEffect, useRef, useState } from 'react';
import { PORTRAIT_ASPECT_RATIO, clamp, MAX_ZOOM, MIN_ZOOM } from '../../utils/perspectiveCropperUtils';

type PerspectiveCropperStateChange = { loading?: boolean };

type UsePerspectiveCropperInteractionParams = {
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  previewCanvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
  overlayRef: React.MutableRefObject<HTMLDivElement | null>;
  naturalSize: { width: number; height: number } | null;
  zoomRef: React.MutableRefObject<number>;
  panRef: React.MutableRefObject<{ x: number; y: number }>;
  getCropRect: () => { x: number; y: number; width: number; height: number } | null;
  clampPan: () => void;
  renderPreview: () => void;
  onDragStop?: (state: PerspectiveCropperStateChange) => void;
};

type UsePerspectiveCropperInteractionResult = {
  frameSize: { width: number; height: number } | null;
  handlePointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  handlePointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
  handlePointerUp: (event: React.PointerEvent<HTMLDivElement>) => void;
  handlePointerLeave: (event: React.PointerEvent<HTMLDivElement>) => void;
};

export const usePerspectiveCropperInteraction = ({
  containerRef,
  previewCanvasRef,
  overlayRef,
  naturalSize,
  zoomRef,
  panRef,
  getCropRect,
  clampPan,
  renderPreview,
  onDragStop,
}: UsePerspectiveCropperInteractionParams): UsePerspectiveCropperInteractionResult => {
  const [frameSize, setFrameSize] = useState<{ width: number; height: number } | null>(null);
  const isDraggingRef = useRef(false);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);
  const pointerIdRef = useRef<number | null>(null);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!frameSize || !naturalSize) {
        return;
      }
      isDraggingRef.current = true;
      pointerIdRef.current = event.pointerId;
      lastPointerRef.current = { x: event.clientX, y: event.clientY };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [frameSize, naturalSize],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current || !frameSize || !naturalSize || !lastPointerRef.current) {
        return;
      }
      event.preventDefault();
      const deltaX = event.clientX - lastPointerRef.current.x;
      const deltaY = event.clientY - lastPointerRef.current.y;
      lastPointerRef.current = { x: event.clientX, y: event.clientY };
      const cropRect = getCropRect();
      if (!cropRect) {
        return;
      }
      const pxPerFrameX = cropRect.width / frameSize.width;
      const pxPerFrameY = cropRect.height / frameSize.height;
      panRef.current = {
        x: panRef.current.x - deltaX * pxPerFrameX,
        y: panRef.current.y - deltaY * pxPerFrameY,
      };
      clampPan();
      renderPreview();
    },
    [clampPan, frameSize, getCropRect, naturalSize, panRef, renderPreview],
  );

  const endDragging = useCallback((target: EventTarget & HTMLDivElement) => {
    if (pointerIdRef.current !== null) {
      target.releasePointerCapture(pointerIdRef.current);
      pointerIdRef.current = null;
    }
    isDraggingRef.current = false;
    lastPointerRef.current = null;
    onDragStop?.({ loading: false });
  }, [onDragStop]);

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current) {
        return;
      }
      endDragging(event.currentTarget);
    },
    [endDragging],
  );

  const handlePointerLeave = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current) {
        return;
      }
      endDragging(event.currentTarget);
    },
    [endDragging],
  );

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      if (!naturalSize) {
        return;
      }
      event.preventDefault();
      const delta = -event.deltaY * 0.001;
      if (delta === 0) {
        return;
      }
      const nextZoom = clamp(zoomRef.current + delta, MIN_ZOOM, MAX_ZOOM);
      if (Math.abs(nextZoom - zoomRef.current) < 0.001) {
        return;
      }
      zoomRef.current = nextZoom;
      clampPan();
      renderPreview();
    },
    [clampPan, naturalSize, renderPreview, zoomRef],
  );

  useEffect(() => {
    if (!previewCanvasRef.current || !containerRef.current) return;

    const updateFrameSize = () => {
      if (!previewCanvasRef.current || !containerRef.current) return;

      const canvas = previewCanvasRef.current;
      const canvasRect = canvas.getBoundingClientRect();

      const canvasDisplayWidth = canvasRect.width;
      const canvasDisplayHeight = canvasRect.height;

      const aspectRatio = PORTRAIT_ASPECT_RATIO;

      let frameWidth = canvasDisplayWidth;
      let frameHeight = frameWidth / aspectRatio;

      if (frameHeight > canvasDisplayHeight) {
        frameHeight = canvasDisplayHeight;
        frameWidth = frameHeight * aspectRatio;
      }

      const nextSize = { width: frameWidth, height: frameHeight };
      setFrameSize((prev) => {
        if (prev && Math.abs(prev.width - nextSize.width) < 0.5 && Math.abs(prev.height - nextSize.height) < 0.5) {
          return prev;
        }
        return nextSize;
      });
    };

    updateFrameSize();

    const resizeObserver = new ResizeObserver(updateFrameSize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    if (previewCanvasRef.current) {
      resizeObserver.observe(previewCanvasRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, previewCanvasRef, naturalSize]);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) {
      return;
    }
    overlay.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      overlay.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel, overlayRef]);

  return {
    frameSize,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerLeave,
  };
};
